import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WITH_CREDS = { withCredentials: true };

const TABS = [
    { id: "partners", label: "Operatori", endpoint: "/admin/partners", csv: "/admin/export/partners.csv" },
    { id: "waitlist", label: "Waitlist turisti", endpoint: "/admin/waitlist", csv: "/admin/export/waitlist.csv" },
    { id: "analytics", label: "Analytics", endpoint: null, csv: null },
];

export default function AdminDashboard() {
    const nav = useNavigate();
    const [tab, setTab] = useState("partners");
    const [data, setData] = useState({ total: 0, items: [], page: 1, pages: 1, per_page: 25 });
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [hideTest, setHideTest] = useState(true);

    const logout = useCallback(() => {
        axios.post(`${API}/admin/logout`, {}, WITH_CREDS).catch(() => { /* best-effort */ });
        nav("/admin/login", { replace: true });
    }, [nav]);

    // debounce search input
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(t);
    }, [search]);

    // reset to page 1 when changing tab, per_page, search, or hideTest
    useEffect(() => {
        setPage(1);
    }, [tab, perPage, debouncedSearch, hideTest]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setErr("");
        const t = TABS.find((x) => x.id === tab);
        if (!t.endpoint) {
            setLoading(false);
            return () => { alive = false; };
        }
        const params = new URLSearchParams({
            page: String(page),
            per_page: String(perPage),
        });
        if (debouncedSearch) params.set("q", debouncedSearch);
        if (hideTest) params.set("hide_test", "true");
        axios.get(`${API}${t.endpoint}?${params.toString()}`, WITH_CREDS)
            .then(({ data }) => { if (alive) setData(data); })
            .catch((ex) => {
                if (!alive) return;
                if (ex?.response?.status === 401) return logout();
                setErr("Errore nel caricamento dei dati.");
            })
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [tab, page, perPage, debouncedSearch, hideTest, logout]);

    const deleteItem = async (id) => {
        if (!window.confirm("Eliminare definitivamente questa candidatura? L'operazione non è reversibile.")) return;
        try {
            const endpoint = tab === "waitlist" ? "/admin/waitlist" : "/admin/partners";
            await axios.delete(`${API}${endpoint}/${id}`, WITH_CREDS);
            // Refresh the list — trigger a state-only refetch by bumping page then restoring
            setData((prev) => ({ ...prev, items: prev.items.filter((x) => x.id !== id), total: Math.max(0, prev.total - 1) }));
        } catch (ex) {
            const detail = ex?.response?.data?.detail || "Eliminazione fallita";
            alert(detail);
        }
    };

    const downloadCsv = async () => {
        const t = TABS.find((x) => x.id === tab);
        if (!t.csv) return;
        const res = await axios.get(`${API}${t.csv}`, { ...WITH_CREDS, responseType: "blob" });
        const blob = new Blob([res.data], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `itly-${tab}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const activeTab = TABS.find((t) => t.id === tab);
    const isAnalytics = tab === "analytics";

    return (
        <div data-testid="admin-dashboard" style={{ minHeight: "100vh", background: "var(--itly-cream)" }}>
            <header style={{ padding: "18px 32px", background: "rgba(247,244,238,0.96)", borderBottom: "1px solid var(--itly-border)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24, position: "sticky", top: 0, zIndex: 30 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <img src="/assets/itly/logo-turista.png" alt="ITLY" style={{ width: 36, height: 36, borderRadius: 10, background: "#8BAFC7" }} />
                    <span className="itly-wordmark" style={{ fontSize: 20 }}>ITLY <span style={{ color: "var(--itly-muted)", fontWeight: 500 }}>Admin</span></span>
                </div>
                <button onClick={logout} className="itly-btn-secondary" data-testid="admin-logout" style={{ padding: "8px 14px", fontSize: 13.5 }}>
                    Esci <span className="arrow" aria-hidden="true">→</span>
                </button>
            </header>

            <div style={{ maxWidth: 1200, margin: "0 auto", padding: "40px 32px 80px" }}>
                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
                    <div>
                        <p style={{ fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--itly-orange-700)", margin: 0 }}>
                            Dashboard
                        </p>
                        <h1 className="itly-serif" style={{ margin: "10px 0 0", fontSize: 40, color: "var(--itly-teal-900)", letterSpacing: "-0.02em" }}>
                            {activeTab.label}
                            {!isAnalytics && (
                                <span style={{ color: "var(--itly-orange-500)", fontFamily: "Fraunces, serif", fontSize: 36, marginLeft: 12 }}>{data.total}</span>
                            )}
                        </h1>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        {TABS.map((t) => (
                            <button key={t.id} onClick={() => setTab(t.id)} className={`itly-chip-light ${tab === t.id ? "on" : ""}`} data-testid={`admin-tab-${t.id}`}>
                                {t.label}
                            </button>
                        ))}
                        {!isAnalytics && (
                            <button onClick={downloadCsv} className="itly-btn-primary" data-testid="admin-export-csv" style={{ padding: "10px 16px", fontSize: 13.5 }}>
                                Export CSV
                            </button>
                        )}
                    </div>
                </div>

                {isAnalytics ? (
                    <AnalyticsPanel onUnauthorized={logout} />
                ) : (
                    <>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            marginBottom: 18,
                            flexWrap: "wrap",
                        }}>
                            <div style={{ position: "relative", flex: "1 1 260px", maxWidth: 380 }}>
                                <span aria-hidden="true" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--itly-muted)" }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.35-4.35"/></svg>
                                </span>
                                <input
                                    type="search"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder={tab === "partners" ? "Cerca per nome, attività, città, email…" : "Cerca per nome, email, città, codice…"}
                                    data-testid="admin-search"
                                    style={{
                                        width: "100%",
                                        padding: "11px 14px 11px 40px",
                                        fontFamily: "Inter, sans-serif",
                                        fontSize: 14,
                                        borderRadius: 12,
                                        border: "1px solid var(--itly-border)",
                                        background: "#ffffff",
                                        color: "var(--itly-teal-900)",
                                        outline: "none",
                                    }}
                                />
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 14, fontFamily: "Inter, sans-serif", fontSize: 13, color: "var(--itly-muted)", flexWrap: "wrap" }}>
                                <label
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 8,
                                        cursor: "pointer",
                                        padding: "6px 12px",
                                        borderRadius: 999,
                                        background: hideTest ? "var(--itly-teal-050)" : "transparent",
                                        border: `1px solid ${hideTest ? "var(--itly-teal-900)" : "var(--itly-border)"}`,
                                        color: "var(--itly-teal-900)",
                                        fontWeight: 600,
                                        letterSpacing: "0.01em",
                                        transition: "all 180ms ease",
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={hideTest}
                                        onChange={(e) => setHideTest(e.target.checked)}
                                        data-testid="admin-hide-test-toggle"
                                        style={{ accentColor: "var(--itly-teal-900)", cursor: "pointer" }}
                                    />
                                    Nascondi test
                                </label>
                                <label htmlFor="per-page" style={{ letterSpacing: "0.02em" }}>Per pagina</label>
                                <select
                                    id="per-page"
                                    value={perPage}
                                    onChange={(e) => setPerPage(Number(e.target.value))}
                                    data-testid="admin-per-page"
                                    style={{
                                        padding: "8px 10px",
                                        borderRadius: 10,
                                        border: "1px solid var(--itly-border)",
                                        background: "#ffffff",
                                        color: "var(--itly-teal-900)",
                                        fontFamily: "Inter, sans-serif",
                                        fontSize: 13,
                                        outline: "none",
                                    }}
                                >
                                    {[10, 25, 50, 100].map((n) => (
                                        <option key={n} value={n}>{n}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {loading && <p style={{ fontFamily: "Inter, sans-serif", color: "var(--itly-muted)" }}>Caricamento...</p>}
                        {err && <p style={{ fontFamily: "Inter, sans-serif", color: "var(--itly-orange-700)" }}>{err}</p>}

                        {!loading && !err && (
                            <>
                                <div className="itly-card" data-testid="admin-table-wrap" style={{ padding: 0, overflow: "hidden" }}>
                                    <div style={{ overflowX: "auto" }}>
                                        <table data-testid="admin-table" style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Inter, sans-serif", fontSize: 13.5 }}>
                                            {tab === "partners" ? <PartnerHeaders /> : <WaitlistHeaders />}
                                            <tbody>
                                                {data.items.length === 0 && (
                                                    <tr><td colSpan={8} style={{ padding: 32, textAlign: "center", color: "var(--itly-muted)" }}>
                                                        {debouncedSearch ? "Nessun risultato per la ricerca." : "Nessun record."}
                                                    </td></tr>
                                                )}
                                                {data.items.map((r) => (
                                                    <tr key={r.id} style={{ borderTop: "1px solid var(--itly-border)" }}>
                                                        {tab === "partners" ? <PartnerRow r={r} onDelete={deleteItem} /> : <WaitlistRow r={r} onDelete={deleteItem} />}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <Pagination
                                    page={data.page || 1}
                                    pages={data.pages || 1}
                                    total={data.total || 0}
                                    perPage={data.per_page || perPage}
                                    onChange={setPage}
                                />
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

const th = { textAlign: "left", padding: "14px 16px", background: "#FCFAF5", fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--itly-muted)" };
const td = { padding: "14px 16px", color: "var(--itly-teal-900)", verticalAlign: "top" };

function PartnerHeaders() {
    return (<thead><tr>{["Data", "Referente", "Attività", "Tipologia", "Luogo", "Email", "Telefono", ""].map((h, i) => <th key={h || `col-${i}`} style={th}>{h}</th>)}</tr></thead>);
}
function PartnerRow({ r, onDelete }) {
    return (<>
        <td style={{ ...td, color: "var(--itly-muted)", whiteSpace: "nowrap" }}>{formatDate(r.created_at)}</td>
        <td style={td}>{r.referente}</td>
        <td style={td}><strong>{r.attivita}</strong>{r.messaggio && <div style={{ marginTop: 4, color: "var(--itly-muted)", fontSize: 12.5 }}>{r.messaggio}</div>}</td>
        <td style={td}><span className="itly-pill" style={{ padding: "3px 10px", background: "var(--itly-teal-050)", color: "var(--itly-teal-900)", fontSize: 12, fontWeight: 600 }}>{r.tipologia}</span></td>
        <td style={td}>{r.citta} · {r.regione}</td>
        <td style={{ ...td, wordBreak: "break-all" }}>{r.email}</td>
        <td style={td}>{r.telefono}</td>
        <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
            <DeleteButton onClick={() => onDelete(r.id)} testid={`delete-partner-${r.id}`} />
        </td>
    </>);
}
function WaitlistHeaders() {
    return (<thead><tr>{["Data", "Nome", "Email", "Città", "Interessi", "Ref. code", "Invitato da", ""].map((h, i) => <th key={h || `col-${i}`} style={th}>{h}</th>)}</tr></thead>);
}
function WaitlistRow({ r, onDelete }) {
    return (<>
        <td style={{ ...td, color: "var(--itly-muted)", whiteSpace: "nowrap" }}>{formatDate(r.created_at)}</td>
        <td style={td}>{r.nome}</td>
        <td style={{ ...td, wordBreak: "break-all" }}>{r.email}</td>
        <td style={td}>{r.citta || "—"}</td>
        <td style={td}>{(r.interessi || []).join(", ") || "—"}</td>
        <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5 }}>{r.referral_code || "—"}</td>
        <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5, color: r.referred_by ? "var(--itly-orange-700)" : "var(--itly-muted)" }}>{r.referred_by || "—"}</td>
        <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
            <DeleteButton onClick={() => onDelete(r.id)} testid={`delete-waitlist-${r.id}`} />
        </td>
    </>);
}

function DeleteButton({ onClick, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            title="Elimina"
            aria-label="Elimina"
            style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 32, height: 32, borderRadius: 8,
                background: "transparent", border: "1px solid transparent",
                color: "var(--itly-muted)", cursor: "pointer",
                transition: "all 160ms ease",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = "#FDEEE7";
                e.currentTarget.style.color = "var(--itly-orange-700)";
                e.currentTarget.style.borderColor = "rgba(232,113,76,0.28)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "var(--itly-muted)";
                e.currentTarget.style.borderColor = "transparent";
            }}
        >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18" />
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <path d="m19 6-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
            </svg>
        </button>
    );
}
function formatDate(s) {
    try {
        const d = typeof s === "string" ? new Date(s) : s;
        return d.toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
        return String(s);
    }
}

/* Pagination controls */
function Pagination({ page, pages, total, perPage, onChange }) {
    if (pages <= 1 && total <= perPage) {
        return (
            <div style={{ marginTop: 14, textAlign: "right", fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "var(--itly-muted)" }}>
                {total} risultat{total === 1 ? "o" : "i"}
            </div>
        );
    }
    const start = (page - 1) * perPage + 1;
    const end = Math.min(page * perPage, total);
    const window = getPageWindow(page, pages);
    return (
        <div
            data-testid="admin-pagination"
            style={{
                marginTop: 18,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
                color: "var(--itly-muted)",
            }}
        >
            <div>
                {start}–{end} di <strong style={{ color: "var(--itly-teal-900)" }}>{total}</strong>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <PageBtn disabled={page <= 1} onClick={() => onChange(page - 1)} testid="admin-page-prev" aria-label="Pagina precedente">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                </PageBtn>
                {window.map((p, i) => (
                    p === "…" ? (
                        <span key={`e${i}`} style={{ padding: "0 6px", color: "var(--itly-muted)" }}>…</span>
                    ) : (
                        <PageBtn
                            key={p}
                            active={p === page}
                            onClick={() => onChange(p)}
                            testid={`admin-page-${p}`}
                        >
                            {p}
                        </PageBtn>
                    )
                ))}
                <PageBtn disabled={page >= pages} onClick={() => onChange(page + 1)} testid="admin-page-next" aria-label="Pagina successiva">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </PageBtn>
            </div>
        </div>
    );
}

function PageBtn({ children, onClick, disabled, active, testid, ...rest }) {
    const base = {
        minWidth: 34,
        height: 34,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 10px",
        borderRadius: 10,
        border: "1px solid var(--itly-border)",
        background: active ? "var(--itly-teal-900)" : "#ffffff",
        color: active ? "var(--itly-cream)" : "var(--itly-teal-900)",
        fontFamily: "Inter, sans-serif",
        fontSize: 13,
        fontWeight: active ? 700 : 500,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        transition: "background 180ms ease, color 180ms ease",
    };
    return (
        <button type="button" onClick={onClick} disabled={disabled} data-testid={testid} style={base} {...rest}>
            {children}
        </button>
    );
}

function getPageWindow(page, pages) {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    if (page <= 4) return [1, 2, 3, 4, 5, "…", pages];
    if (page >= pages - 3) return [1, "…", pages - 4, pages - 3, pages - 2, pages - 1, pages];
    return [1, "…", page - 1, page, page + 1, "…", pages];
}

/* ================================
   Analytics Panel
   ================================ */
function AnalyticsPanel({ onUnauthorized }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        let alive = true;
        axios.get(`${API}/admin/analytics/summary`, WITH_CREDS)
            .then(({ data }) => { if (alive) setData(data); })
            .catch((ex) => {
                if (!alive) return;
                if (ex?.response?.status === 401) return onUnauthorized();
                setErr("Errore nel caricamento delle analytics.");
            })
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [onUnauthorized]);

    if (loading) return <p style={{ fontFamily: "Inter, sans-serif", color: "var(--itly-muted)" }}>Caricamento analytics...</p>;
    if (err) return <p style={{ fontFamily: "Inter, sans-serif", color: "var(--itly-orange-700)" }}>{err}</p>;
    if (!data) return null;

    const t = data.totals || {};
    const daily = data.daily_14d || [];
    const maxDaily = Math.max(1, ...daily.map((d) => d.views));

    return (
        <div data-testid="admin-analytics" style={{ display: "grid", gap: 20 }}>
            {/* KPI row */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
                <Kpi label="Visite totali" value={t.all_time_views ?? 0} testid="kpi-total-views" />
                <Kpi label="Ultime 24h" value={t.views_24h ?? 0} testid="kpi-24h" accent />
                <Kpi label="Ultimi 7 giorni" value={t.views_7d ?? 0} testid="kpi-7d" />
                <Kpi label="Visitatori unici (7g)" value={t.unique_visitors_7d ?? 0} testid="kpi-unique-7d" />
                <Kpi label="Iscritti da referral" value={t.referral_waitlist_conversions ?? 0} testid="kpi-referral" accent />
            </div>

            {/* Daily chart */}
            <div className="itly-card" style={{ padding: 22 }}>
                <SectionTitle title="Visite giornaliere · ultimi 14 giorni" testid="analytics-daily-title" />
                <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 140, marginTop: 20 }} data-testid="analytics-daily-chart">
                    {daily.map((d) => {
                        const h = Math.max(4, Math.round((d.views / maxDaily) * 130));
                        const isToday = daily[daily.length - 1]?.day === d.day;
                        return (
                            <div key={d.day} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }} title={`${d.day}: ${d.views} visite`}>
                                <div
                                    style={{
                                        width: "100%",
                                        maxWidth: 34,
                                        height: h,
                                        background: isToday ? "var(--itly-orange-500)" : "var(--itly-teal-600)",
                                        borderRadius: 6,
                                        transition: "background 200ms ease",
                                    }}
                                />
                                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 10.5, color: "var(--itly-muted)" }}>
                                    {d.day.slice(8, 10)}/{d.day.slice(5, 7)}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Two-column tables */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
                <ListCard title="Pagine più visitate · 30g" rows={data.top_pages_30d} labelKey="path" testid="analytics-top-pages" empty="Nessuna visita registrata ancora." />
                <ListCard title="Sorgenti di traffico · 30g" rows={data.top_referrers_30d} labelKey="referrer" testid="analytics-top-referrers" empty="Nessun referrer esterno rilevato." />
                <ListCard title="Sorgenti UTM · 30g" rows={data.top_utm_30d} labelKey="source" testid="analytics-top-utm" empty="Nessun UTM tag utilizzato." />
                <ReferralCodesCard rows={data.top_referral_codes} testid="analytics-top-referral-codes" />
            </div>
        </div>
    );
}

function Kpi({ label, value, accent, testid }) {
    return (
        <div className="itly-card" data-testid={testid} style={{ padding: 20 }}>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11.5, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--itly-muted)", marginBottom: 8 }}>
                {label}
            </div>
            <div className="itly-serif" style={{ fontSize: 34, lineHeight: 1, color: accent ? "var(--itly-orange-500)" : "var(--itly-teal-900)", letterSpacing: "-0.02em" }}>
                {value}
            </div>
        </div>
    );
}

function SectionTitle({ title, testid }) {
    return (
        <div data-testid={testid} style={{ fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--itly-teal-900)" }}>
            {title}
        </div>
    );
}

function ListCard({ title, rows, labelKey, testid, empty }) {
    const list = rows || [];
    return (
        <div className="itly-card" data-testid={testid} style={{ padding: 22 }}>
            <SectionTitle title={title} />
            <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
                {list.length === 0 && (
                    <p style={{ fontFamily: "Inter, sans-serif", fontSize: 13.5, color: "var(--itly-muted)", margin: "8px 0 0" }}>
                        {empty}
                    </p>
                )}
                {list.map((r, i) => (
                    <div key={String(r[labelKey] || `row-${i}`)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 0", borderTop: i === 0 ? "none" : "1px solid var(--itly-border)" }}>
                        <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13.5, color: "var(--itly-teal-900)", wordBreak: "break-all", flex: 1, minWidth: 0 }}>
                            {String(r[labelKey] || "—")}
                        </span>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "var(--itly-orange-700)", fontWeight: 600, flexShrink: 0 }}>
                            {r.count}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ReferralCodesCard({ rows, testid }) {
    const list = rows || [];
    return (
        <div className="itly-card" data-testid={testid} style={{ padding: 22 }}>
            <SectionTitle title="Top ambasciatori · referral" />
            <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
                {list.length === 0 && (
                    <p style={{ fontFamily: "Inter, sans-serif", fontSize: 13.5, color: "var(--itly-muted)", margin: "8px 0 0" }}>
                        Nessun referral registrato ancora — condividi il tuo link!
                    </p>
                )}
                {list.map((r, i) => (
                    <div key={r.code} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 0", borderTop: i === 0 ? "none" : "1px solid var(--itly-border)" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13.5, color: "var(--itly-teal-900)", fontWeight: 600 }}>
                                {r.nome || "—"}
                            </div>
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11.5, color: "var(--itly-muted)" }}>
                                {r.code}
                            </div>
                        </div>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "var(--itly-orange-700)", fontWeight: 600 }}>
                            {r.count} {r.count === 1 ? "invito" : "inviti"}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}
